import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Parse from 'parse'
import BoundedListComponent from '../Shared/BoundedListComponent'
import DetailSectionComponent from '../Shared/DetailSectionComponent'
import {
  EMPTY,
  formatDate,
  formatDateTime,
  formatNumber
} from '../Shared/detailFormat'

const userPointer = (memberId) => Parse.User.createWithoutData(memberId)

const QUERY_LIMIT = 100

// Member-scoped queries for the panels below, keyed for useMemberQuery.
const QUERIES = {
  assignments: (memberId) => {
    const query = new Parse.Query('ExperimentAssignment')
    query.equalTo('user', userPointer(memberId))
    query.include('experiment')
    query.descending('assignedAt')
    query.limit(QUERY_LIMIT)
    return query
  },
  challenges: (memberId) => {
    const query = new Parse.Query('ChallengeProgress')
    query.equalTo('userId', memberId)
    query.include('challenge')
    query.descending('updatedAt')
    query.limit(QUERY_LIMIT)
    return query
  },
  scheduled: (memberId) => {
    // ScheduledWorkout.date is a YYYY-MM-DD string.
    const today = new Date().toISOString().slice(0, 10)
    const query = new Parse.Query('ScheduledWorkout')
    query.equalTo('userId', memberId)
    query.greaterThanOrEqualTo('date', today)
    query.ascending('date')
    query.limit(14)
    return query
  }
}

// null while loading, undefined on failure, so one class the dashboard can't
// read blanks its own panel rather than the page.
const useMemberQuery = (queryKey, memberId) => {
  const [rows, setRows] = useState(null)
  useEffect(() => {
    let isCurrent = true
    setRows(null)
    QUERIES[queryKey](memberId).find().then(
      (results) => {
        if (isCurrent) setRows(results)
      },
      (error) => {
        console.error(`Error loading ${queryKey}: ` + error.message)
        if (isCurrent) setRows(undefined)
      }
    )
    return () => { isCurrent = false }
  }, [queryKey, memberId])
  return rows
}

const LoadState = ({ rows, emptyText }) => {
  if (rows === null) return <p className="text-muted mb-0">Loading...</p>
  if (rows === undefined) return <p className="text-danger mb-0">Couldn&apos;t load</p>
  return <p className="text-muted mb-0">{emptyText}</p>
}

LoadState.propTypes = {
  rows: PropTypes.array,
  emptyText: PropTypes.string.isRequired
}

const hasRows = (rows) => Array.isArray(rows) && rows.length > 0

// Collapsed-header summary: a count once loaded, nothing while loading.
const countSummary = (rows, limit) => {
  if (rows === null) return null
  if (rows === undefined) return "couldn't load"
  return rows.length >= limit ? `${limit}+` : String(rows.length)
}

// Rows in other classes keyed to the member: experiment buckets, challenge
// progress and upcoming scheduled plan workouts. (NextWorkout would belong
// here too, but that class is master-key only.) Renders the three sections
// alone so the member screen can place them in its columns.
const MemberActivityDataComponent = ({ memberId }) => {
  const assignments = useMemberQuery('assignments', memberId)
  const challenges = useMemberQuery('challenges', memberId)
  const scheduled = useMemberQuery('scheduled', memberId)

  return (
    <>
      <DetailSectionComponent id="member-experiments" title="Experiments" collapsible
        subtitle={countSummary(assignments, QUERY_LIMIT)}>
        {hasRows(assignments)
          ? (
            <BoundedListComponent items={assignments} noun="assignments">
              {(visible) => (
                <table className="table table-sm mb-0">
                  <thead>
                    <tr><th>Experiment</th><th>Variant</th><th>Assigned</th><th>Source</th></tr>
                  </thead>
                  <tbody>
                    {visible.map((row) => (
                      <tr key={row.id}>
                        <td>
                          {row.get('experiment')
                            ? <a href={'/#/experiments/' + row.get('experiment').id}>{row.get('experimentKey')}</a>
                            : row.get('experimentKey')}
                        </td>
                        <td><b>{row.get('variant')}</b></td>
                        <td>{formatDateTime(row.get('assignedAt'))}</td>
                        <td className="small text-muted">{row.get('source')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </BoundedListComponent>
            )
          : <LoadState rows={assignments} emptyText="Not in any experiment" />}
      </DetailSectionComponent>
      <DetailSectionComponent id="member-challenges" title="Challenges" collapsible
        subtitle={countSummary(challenges, QUERY_LIMIT)}>
        {hasRows(challenges)
          ? (
            <BoundedListComponent items={challenges} noun="challenges">
              {(visible) => (
                <table className="table table-sm mb-0">
                  <thead>
                    <tr><th>Challenge</th><th>Progress</th><th>Updated</th></tr>
                  </thead>
                  <tbody>
                    {visible.map((row) => (
                      <tr key={row.id}>
                        <td>
                          {row.get('challenge')
                            ? <a href={'/#/challenges/' + row.get('challenge').id}>{row.get('challenge').get('title') || row.get('challenge').id}</a>
                            : EMPTY}
                          {row.get('isPrivate') && <span className="badge text-bg-light border ms-1">private</span>}
                        </td>
                        <td>{formatNumber(row.get('progress'), 1)}</td>
                        <td>{formatDate(row.updatedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </BoundedListComponent>
            )
          : <LoadState rows={challenges} emptyText="No challenge progress" />}
      </DetailSectionComponent>
      <DetailSectionComponent id="member-scheduled" title="Upcoming scheduled workouts" collapsible
        subtitle={hasRows(scheduled) ? `${scheduled.length} · next ${scheduled[0].get('date')}` : countSummary(scheduled, 14)}>
        {hasRows(scheduled)
          ? (
            <ul className="mb-0">
              {scheduled.map((row) => (
                <li key={row.id}>
                  {row.get('date')}{' '}
                  <small className="text-muted">plan entry {row.get('trainingPlanEntryId') || EMPTY}</small>
                  {row.get('completedWorkout') && (
                    <a className="ms-1" href={'/#/completedWorkouts/' + row.get('completedWorkout').id}>completed</a>
                  )}
                </li>
              ))}
            </ul>
            )
          : <LoadState rows={scheduled} emptyText="Nothing scheduled" />}
      </DetailSectionComponent>
    </>
  )
}

MemberActivityDataComponent.propTypes = {
  memberId: PropTypes.string.isRequired
}

export default MemberActivityDataComponent
